import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, FileText, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Layout";
import { Panel, itDate } from "@/components/kit";
import { AddGuests, GuestRows, countGuests } from "@/components/GuestsPanel";
import { API, api, errMsg, fmtMoney } from "@/lib/api";
import { GalleryStrip } from "@/components/Gallery";
import { LogosRow } from "@/components/BrandSettings";

function Countdown({ date }) {
  const days = Math.ceil((new Date(date + "T12:00") - new Date()) / 864e5);
  if (!date) return null;
  return (
    <div className="text-center" data-testid="portal-countdown">
      <div className="font-serif-it text-7xl sm:text-8xl font-semibold text-white">{days > 0 ? days : days === 0 ? "Oggi!" : "✓"}</div>
      <div className="text-xs uppercase tracking-[0.3em] text-white/70 mt-1">{days > 0 ? (days === 1 ? "giorno al grande evento" : "giorni al grande evento") : days === 0 ? "è il grande giorno" : "evento concluso: grazie!"}</div>
    </div>
  );
}

function PayStatus({ token, reload }) {
  const [params, setParams] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState(sid ? "checking" : "");
  useEffect(() => {
    if (!sid) return;
    let n = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          reload();
          setParams({});
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) return setState("failed");
      } catch (e) {
        return setState("failed");
      }
      if (++n < 15) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);
  if (!state) return null;
  const txt = { checking: "Verifica del pagamento in corso…", paid: "Pagamento ricevuto, grazie! La ricevuta è a disposizione presso lo staff.", failed: "Pagamento non completato. Può riprovare quando vuole.", pending: "Il pagamento è in elaborazione: aggiorni la pagina tra qualche minuto." }[state];
  return <div className={`rounded-xl p-4 text-sm ${state === "paid" ? "bg-[#DCFCE7] text-[#166534]" : state === "failed" ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#EAF5F7] text-[#0B6E8F]"}`} data-testid="portal-pay-status">{txt}</div>;
}

export default function ClientArea() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [guests, setGuests] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/public/portal/${token}`).then((r) => { setD(r.data); setGuests(r.data.guests); }).catch((e) => setErr(errMsg(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const pay = async (rata) => {
    setBusy(rata);
    try {
      const r = await api.post(`/public/portal/${token}/checkout`, { rata, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };
  const saveGuests = async () => {
    try {
      await api.put(`/public/portal/${token}/guests`, { guests });
      toast.success("Lista ospiti inviata allo staff, grazie!");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="portal-error"><div><Brand /><p className="mt-6 text-[#47606B]">{err}</p></div></div>;
  if (!d) return <div className="p-10 text-sm text-[#7B929C]">Caricamento…</div>;
  const cnt = countGuests(guests);
  return (
    <div className="min-h-screen pb-16" data-testid="client-area">
      <header className="relative overflow-hidden bg-[#0D2B3A]">
        <img src="/img/mare.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
          <div className="inline-block rounded-xl bg-white/90 px-4 py-3"><Brand small /></div>
          <LogosRow light />
          <div className="grid md:grid-cols-2 gap-8 items-end">
            <div className="text-white fade-up">
              <div className="text-xs uppercase tracking-[0.3em] text-white/70">La sua area riservata</div>
              <h1 className="font-serif-it text-4xl sm:text-5xl lg:text-6xl font-semibold mt-2" data-testid="portal-client">{d.cliente_nome}</h1>
              <div className="mt-2 text-white/80 text-sm">{d.evento_tipo} • {itDate(d.evento_data)}{d.ora_inizio && ` ore ${d.ora_inizio}`} • {d.sede}</div>
            </div>
            <Countdown date={d.evento_data} />
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 -mt-4 relative space-y-6">
        <PayStatus token={token} reload={load} />
        <Panel title="Pagamenti" testId="portal-payments">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="text-xs text-[#7B929C]">Totale</div><b>{fmtMoney(d.plan.totale)}</b></div>
            <div><div className="text-xs text-[#7B929C]">Versato</div><b className="text-[#166534]" data-testid="portal-paid">{fmtMoney(d.plan.pagato)}</b></div>
            <div><div className="text-xs text-[#7B929C]">Residuo</div><b>{fmtMoney(d.plan.residuo)}</b></div>
          </div>
          {d.plan.rate.map((r) => (
            <div key={r.key} className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EAF5F7] pt-3 text-sm" data-testid={`portal-rata-${r.key}`}>
              <div><b>{r.label}</b><div className="text-xs text-[#7B929C]">{fmtMoney(r.importo)}{r.scadenza && ` • entro il ${itDate(r.scadenza)}`}</div></div>
              {r.residuo > 0 ? (d.can_pay ? <Button size="sm" className="rounded-full" disabled={!!busy} onClick={() => pay(r.key)} data-testid={`portal-pay-${r.key}`}>{busy === r.key ? <Loader2 size={14} className="animate-spin mr-1" /> : <CreditCard size={14} className="mr-1" />} Paga {fmtMoney(r.residuo)} con carta</Button> : <span className="text-xs text-[#7B929C]">da pagare</span>) : <span className="text-xs px-2 py-1 rounded-full bg-[#DCFCE7] text-[#166534]">pagata</span>}
            </div>
          ))}
          {d.payments.length > 0 && <div className="text-xs text-[#47606B] border-t border-[#EAF5F7] pt-3 space-y-1">{d.payments.map((p) => <div key={p.numero}>✓ {itDate(p.data)} • {fmtMoney(p.importo)} • {p.metodo}</div>)}</div>}
        </Panel>
        <div className="grid md:grid-cols-2 gap-6">
          <Panel title="Il suo menù" testId="portal-menu">
            {d.formula && <div className="text-xs uppercase tracking-wider text-[#34B3B0]">{d.formula}</div>}
            {d.menu.length === 0 && <div className="text-sm text-[#7B929C]">Il menù sarà disponibile a breve.</div>}
            {d.menu.map((m) => <div key={m.label} className="text-sm"><div className="text-xs text-[#7B929C]">{m.label}</div><div className="whitespace-pre-line">{m.value}</div></div>)}
          </Panel>
          <Panel title="Documenti" testId="portal-docs">
            <a href={`${API}/public/portal/${token}/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#0B6E8F] hover:underline" data-testid="portal-contract-pdf"><FileText size={15} /> Contratto n. {d.number} {d.status === "firmato" ? "(firmato)" : "(da firmare)"}</a>
          </Panel>
        </div>
        <GalleryStrip items={d.gallery} sede={d.sede} />
        {d.timeline?.length > 0 && (
          <Panel title="La scaletta della giornata" testId="portal-timeline">
            <div className="space-y-2">{d.timeline.map((t, i) => <div key={i} className="flex gap-4 text-sm"><span className="font-serif-it text-lg text-[#0B6E8F] w-14 shrink-0">{t.ora}</span><span className="border-l-2 border-[#9FE3E0] pl-3">{t.attivita}</span></div>)}</div>
          </Panel>
        )}
        {d.rsvp_token && (
          <Panel title="Conferme dei vostri invitati" testId="portal-rsvp">
            <p className="text-sm text-[#47606B]">Girate questo link nei gruppi di famiglia e amici: ognuno conferma la presenza e indica allergie e bambini, e noi aggiorniamo la lista.</p>
            <div className="flex flex-wrap gap-2">
              <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`💌 Ci farebbe tanto piacere averti con noi! Conferma qui la tua presenza 👉 ${window.location.origin}/conferma/${d.rsvp_token}`)}`} target="_blank" rel="noreferrer" className="rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-medium" data-testid="portal-rsvp-wa">Condividi su WhatsApp</a>
              <Button variant="outline" className="rounded-full" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/conferma/${d.rsvp_token}`).then(() => toast.success("Link copiato"))} data-testid="portal-rsvp-copy">Copia link</Button>
            </div>
          </Panel>
        )}
        <Panel title="Lista invitati, menù e allergie" testId="portal-guests">
          <div className="text-sm text-[#47606B]">{d.guests_locked ? "La lista è definitiva. Per modifiche contatti lo staff." : `Può inserire e modificare gli invitati fino al ${itDate(d.guests_deadline)}. Indichi menù speciali e allergie: li useremo solo per il servizio.`}</div>
          <div className="flex gap-4 text-sm"><span>Adulti <b>{cnt.adulto || 0}</b></span><span>Bambini <b>{cnt.bambino || 0}</b></span><span>Fornitori <b>{cnt.fornitore || 0}</b></span></div>
          <GuestRows guests={guests} setGuests={setGuests} disabled={d.guests_locked} />
          {!d.guests_locked && <AddGuests guests={guests} setGuests={setGuests} />}
          {!d.guests_locked && <Button className="rounded-full" onClick={saveGuests} data-testid="portal-guests-save"><Save size={14} className="mr-2" /> Invia la lista</Button>}
        </Panel>
      </main>
    </div>
  );
}
